import React, { ReactNode } from 'react';

interface CommonCardProps {
    title?: string;
    label?: string;
    value?: string | number;
    icon?: ReactNode;
    iconColor?: string;
    children?: ReactNode;
    className?: string;
    onClick?: () => void;
    headerAction?: ReactNode;
}

export const CommonCard: React.FC<CommonCardProps> = ({
    title,
    label,
    value,
    icon,
    iconColor = '#EF4444',
    children,
    className = '',
    onClick,
    headerAction,
}) => {
    // Determine the layout mode
    const isInfoMode = !!title || !!children;
    const isClickable = !!onClick;

    return (
        <div
            className={`p-5 bg-background border border-border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ${isClickable ? 'cursor-pointer hover:border-primary/40 hover:-translate-y-0.5 active:scale-[0.98]' : ''} ${className}`}
            onClick={onClick}
        >
            {isInfoMode ? (
                /* Information Mode (Title + Icon Top, Children below) */
                <>
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            {icon && <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                style={{
                                    backgroundColor: `${iconColor}15`,
                                    color: iconColor
                                }}
                            >
                                {React.isValidElement(icon) && React.cloneElement(icon as React.ReactElement<any>, {
                                    size: 20,
                                    strokeWidth: 2.5
                                })}
                            </div>}
                            <h3 className="font-semibold text-foreground">{title}</h3>
                        </div>
                        {headerAction && <div className="shrink-0">{headerAction}</div>}
                    </div>
                    <div>{children}</div>
                </>
            ) : (
                /* Metric Mode (Label/Value Left, Icon Right) */
                <div className="flex items-center justify-between gap-4">
                    <div className="space-y-1 flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{label}</p>
                        <h3 className="text-2xl font-extrabold text-foreground tracking-tight truncate">{value}</h3>
                    </div>
                    <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                        style={{
                            backgroundColor: `${iconColor}15`,
                            color: iconColor
                        }}
                    >
                        {React.isValidElement(icon) && React.cloneElement(icon as React.ReactElement<any>, {
                            size: 24,
                            strokeWidth: 2.5
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
